import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useDialogStore from '../store/dialogStore';

export default function DialogHost() {
  const { current, resolveCurrent } = useDialogStore();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!current) return;
    setValue(current.type === 'prompt' ? (current.defaultValue || '') : '');
    setError('');
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [current]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) return null;

  const cancel = () => {
    if (current.type === 'confirm') resolveCurrent(false);
    else if (current.type === 'prompt') resolveCurrent(null);
    else resolveCurrent(undefined);
  };

  const confirm = () => {
    if (current.type === 'prompt') {
      const trimmed = value;
      if (current.minLength && trimmed.length < current.minLength) {
        setError(`Mínimo de ${current.minLength} caracteres.`);
        return;
      }
      resolveCurrent(trimmed);
    } else if (current.type === 'confirm') {
      resolveCurrent(true);
    } else {
      resolveCurrent(undefined);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50"
      style={{ zIndex: 2147483000 }}
      onClick={cancel}
    >
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[420px] max-w-[92vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary pr-4">{current.title}</h3>
          <button onClick={cancel} className="text-text-secondary hover:text-text-primary shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4">
          {current.message && (
            <p className="text-sm text-text-secondary whitespace-pre-wrap">{current.message}</p>
          )}
          {current.type === 'prompt' && (
            <div className={current.message ? 'mt-3' : ''}>
              {current.label && (
                <label className="text-xs text-text-secondary block mb-1">{current.label}</label>
              )}
              <input
                ref={inputRef}
                type={current.inputType}
                value={value}
                onChange={(e) => { setValue(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && confirm()}
                placeholder={current.placeholder}
                className="w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
              />
              {error && <p className="text-[11px] text-danger mt-1">{error}</p>}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border/50 flex justify-end gap-2">
          {current.type !== 'alert' && (
            <button
              onClick={cancel}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
            >
              {current.cancelLabel || 'Cancelar'}
            </button>
          )}
          <button
            ref={current.type === 'alert' ? inputRef : undefined}
            onClick={confirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${current.danger ? 'bg-danger hover:bg-danger/80' : 'bg-accent hover:bg-accent/80'}`}
          >
            {current.confirmLabel || 'OK'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
